// © 2013 - 2023 Tinder, Inc., ALL RIGHTS RESERVED

import rgb2hex from 'rgb-hex';
import hex2rgb, { RgbaObject } from 'hex-rgb';
import { rgb2lab, lab2rgb } from 'rgb-lab';
import { hsluvToHex } from 'hsluv';

import { EasingFunction } from './easing';

const COLOR_WHITE = '#ffffff';
const COLOR_BLACK = '#000000';
function roundTo(num: number, places: number) {
  return Number(num.toFixed(places));
}

function getNormalizedValue(fn: EasingFunction, t: number, t0: number, t1: number) {
  const v = fn(t);
  if (t0 === t1) {
    // Single point
    return t < 0.5 ? 0 : 1;
  }
  const v0 = fn(t0);
  const v1 = fn(t1);
  if (v0 === v1) {
    // Treat flat segment to linear interpolation
    return (t - t0) / (t1 - t0);
  }
  return (v - v0) / (v1 - v0);
}

function getStopIndexes(stops: number[], t: number) {
  const lastStopIndex = stops.length - 1;
  let leftIndex = 0;
  let rightIndex = lastStopIndex;
  stops.forEach((stop, i) => {
    const stopRev = stops[lastStopIndex - i];
    if (t >= stop) {
      leftIndex = i;
    }
    if (t <= stopRev) {
      rightIndex = lastStopIndex - i;
    }
  });
  return [leftIndex, rightIndex];
}

function createStopEasingFn(
  easingFn: number | EasingFunction,
  getNormalizedParam: (color: Color) => number,
  colors: Color[],
  stops: number[]
): number | EasingFunction {
  if (typeof easingFn !== 'function') {
    return easingFn;
  }
  return function stopEasingFn(t) {
    const [leftIndex, rightIndex] = getStopIndexes(stops, t);
    const v0 = getNormalizedParam(colors[leftIndex]);
    const v1 = getNormalizedParam(colors[rightIndex]);
    const t0 = stops[leftIndex];
    const t1 = stops[rightIndex];
    const w = getNormalizedValue(easingFn, t, t0, t1);
    return w * v1 + (1 - w) * v0;
  };
}

function hue2rgb(p: number, q: number, t: number) {
  if (t < 0) {
    t = 1 + (t % 1);
  }
  if (t > 1) {
    t = t % 1;
  }
  if (t < 1 / 6) {
    return p + (q - p) * 6 * t;
  }
  if (t < 1 / 2) {
    return q;
  }
  if (t < 2 / 3) {
    return p + (q - p) * (2 / 3 - t) * 6;
  }
  return p;
}

export class Color {
  private _rgbColor: RgbaObject;
  private _labColor: { l: number; a: number; b: number };

  static hsb(hue: number, saturation: number, brightness: number, normalized = false): Color {
    const h = Math.min(Math.max(0, normalized ? hue : hue / 360), 1);
    const s = Math.min(Math.max(0, normalized ? saturation : saturation / 100), 1);
    const v = Math.min(Math.max(0, normalized ? brightness : brightness / 100), 1);

    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    let r, g, b;
    switch (i % 6) {
      case 0:
        r = v;
        g = t;
        b = p;
        break;
      case 1:
        r = q;
        g = v;
        b = p;
        break;
      case 2:
        r = p;
        g = v;
        b = t;
        break;
      case 3:
        r = p;
        g = q;
        b = v;
        break;
      case 4:
        r = t;
        g = p;
        b = v;
        break;
      case 5:
        r = v;
        g = p;
        b = q;
        break;
    }

    return new Color(`#${rgb2hex(r * 255, g * 255, b * 255, 1)}`);
  }

  static hsl(hue: number, saturation: number, lightness: number, normalized = false): Color {
    const h = Math.min(Math.max(0, normalized ? hue : hue / 360), 1);
    const s = Math.min(Math.max(0, normalized ? saturation : saturation / 100), 1);
    const l = Math.min(Math.max(0, normalized ? lightness : lightness / 100), 1);

    let r, g, b;
    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return new Color(`#${rgb2hex(r * 255, g * 255, b * 255, 1)}`);
  }

  static hsluv(hue: number, saturation: number, lightness: number, normalized = false): Color {
    const h = Math.min(Math.max(0, normalized ? 360 * hue : hue), 360);
    const s = Math.min(Math.max(0, normalized ? 100 * saturation : saturation), 100);
    const l = Math.min(Math.max(0, normalized ? 100 * lightness : lightness), 100);
    return new Color(hsluvToHex([h, s, l]));
  }

  static hsv = Color.hsb;

  static lab(lightness: number, greenRed: number, blueYellow: number, normalized = false): Color {
    const l = normalized ? lightness * 100 : lightness;
    const a = normalized ? greenRed * 255 - 128 : greenRed;
    const b = normalized ? blueYellow * 255 - 128 : blueYellow;

    return new Color({ l, a, b });
  }

  static rgb(red: number, green: number, blue: number, normalized = false): Color {
    const r = normalized ? red * 255 : red;
    const g = normalized ? green * 255 : green;
    const b = normalized ? blue * 255 : blue;

    return new Color(`#${rgb2hex(r, g, b, 1)}`);
  }

  constructor(
    color:
      | string
      | { red: number; green: number; blue: number; alpha?: number }
      | { l: number; a: number; b: number; alpha?: number }
  ) {
    if (typeof color === 'string') {
      this._rgbColor = hex2rgb(color.startsWith('rgb') ? rgb2hex(color) : color);
      const { red, green, blue } = this._rgbColor;
      const [l, a, b] = rgb2lab([red, green, blue]);
      this._labColor = { l, a, b };
    } else if ('l' in color) {
      const { l, a, b, alpha = 1 } = color;
      this._labColor = { l, a, b };
      const [red, green, blue] = lab2rgb([l, a, b]);
      this._rgbColor = { red, green, blue, alpha };
    } else {
      this._rgbColor = { alpha: 1, ...color };
      const { red, green, blue } = this._rgbColor;
      const [l, a, b] = rgb2lab([red, green, blue]);
      this._labColor = { l, a, b };
    }
  }

  alpha(opacity = 1): Color {
    const { red, green, blue } = this._rgbColor;

    return new Color(`#${rgb2hex(red, green, blue, opacity)}`);
  }

  rgb(
    r = this._rgbColor.red,
    g = this._rgbColor.green,
    b = this._rgbColor.blue,
    a = this._rgbColor.alpha
  ): string {
    return `rgba(${roundTo(r, 0)}, ${roundTo(g, 0)}, ${roundTo(b, 0)}, ${roundTo(a, 2)})`;
  }

  rgbaTuple(
    r = this._rgbColor.red,
    g = this._rgbColor.green,
    b = this._rgbColor.blue,
    a = this._rgbColor.alpha
  ): [r: number, g: number, b: number, a: number] {
    return [r, g, b, a];
  }

  hex(): string {
    const { red, green, blue, alpha = 1 } = this._rgbColor;
    return `#${rgb2hex(red, green, blue, alpha)}`;
  }

  /**
   * Returns the shortest possible hex representation of the color that doesm't
   * lose information
   *
   * @returns `#rgb`, `#rrggbb`, or `#rrggbbaa` string
   */
  hexShort(): string {
    const hex = this.hex();
    // Return #rrggbbaa if alpha is not 100%
    if (!hex.endsWith('ff')) {
      return hex;
    }
    // Return #rrggbb if value cannot be shortened
    if (hex[1] !== hex[2] || hex[3] !== hex[4] || hex[5] !== hex[6]) {
      return hex.substring(0, 7);
    }
    // Return #rgb
    return `#${hex[2]}${hex[4]}${hex[6]}`;
  }

  /**
   * Returns rgba(...) when alpha is less than 100% and hex otherwise
   */
  hexAlphaSafe(): string {
    return this._rgbColor.alpha < 1 ? this.rgb() : this.hexShort();
  }

  getRed(): number {
    return this._rgbColor.red;
  }

  getGreen(): number {
    return this._rgbColor.green;
  }

  getBlue(): number {
    return this._rgbColor.blue;
  }

  getAlpha(): number {
    return this._rgbColor.alpha;
  }

  getLabL(): number {
    return this._labColor.l;
  }

  getLabA(): number {
    return this._labColor.a;
  }

  getLabB(): number {
    return this._labColor.b;
  }

  getRelativeLuminance(): number {
    // https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
    const { red, green, blue } = this._rgbColor;
    const [r, g, b] = [red, green, blue]
      .map((value) => value / 255)
      .map((value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  // https://github.com/Qix-/color/blob/master/index.js#L366
  mix(mixinColor: Color, p = 0.5, model: 'rgb' | 'lab' = 'rgb'): Color {
    // ported from sass implementation in C
    // https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
    const color1 = mixinColor;
    const color2 = this; // eslint-disable-line @typescript-eslint/no-this-alias

    const w = 2 * p - 1;
    const a = color1.getAlpha() - color2.getAlpha();

    const w1 = ((w * a === -1 ? w : (w + a) / (1 + w * a)) + 1) / 2.0;
    const w2 = 1 - w1;

    const alpha = color1.getAlpha() * p + color2.getAlpha() * (1 - p);

    if (model === 'lab') {
      const [l, a, b] = [
        w1 * color1.getLabL() + w2 * color2.getLabL(),
        w1 * color1.getLabA() + w2 * color2.getLabA(),
        w1 * color1.getLabB() + w2 * color2.getLabB(),
      ];
      return new Color({ l, a, b, alpha });
    }

    const [red, green, blue] = [
      w1 * color1.getRed() + w2 * color2.getRed(),
      w1 * color1.getGreen() + w2 * color2.getGreen(),
      w1 * color1.getBlue() + w2 * color2.getBlue(),
    ];
    return new Color({ red, green, blue, alpha });
  }
}

/**
 * Mixes all input colors equally
 */
export function averageColors(input: string[]) {
  const colors = input.map((color) => new Color(color));
  let i = 2;
  let color = colors.shift();
  while (colors.length) {
    color = color.mix(colors.shift(), 1 / i++);
  }
  return color.hex();
}

/**
 * Creates a linear mix of two colors in the specified proportion
 */
export function mixColors(a: string, b: string, amount = 0.5): string {
  const colorA = new Color(a);
  const colorB = new Color(b);

  const color = colorA.mix(colorB, amount);
  return color.hexAlphaSafe();
}

/**
 * Creates a lightened version of a color by mixing it with white in the specified proportion
 */
export function lightenColor(color: string, amount = 0.1): string {
  return mixColors(color, COLOR_WHITE, amount);
}

/**
 * Creates a darkened version of a color by mixing it with black in the specified proportion
 */
export function darkenColor(color: string, amount = 0.1): string {
  return mixColors(color, COLOR_BLACK, amount);
}

/**
 * Sets the alpha of the supplied color
 */
export function alphaColor(c: string, opacity = 1): string {
  return new Color(c).alpha(opacity).hexAlphaSafe();
}

/**
 * Sets the alpha of the supplied color
 */
export function rgbaTuple(c: string) {
  return new Color(c).rgbaTuple();
}

/**
 * Gets the contrast ratio
 *
 * @see https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
 */
export function contrastRatio(a: string, b: string): number {
  const colorA = new Color(a);
  const colorB = new Color(b);

  const relativeLumannceA = colorA.getRelativeLuminance();
  const relativeLumannceB = colorB.getRelativeLuminance();

  const lightest = Math.max(relativeLumannceA, relativeLumannceB);
  const darkest = Math.min(relativeLumannceA, relativeLumannceB);

  const contrast = (lightest + 0.05) / (darkest + 0.05);

  return Math.floor(contrast * 100) / 100;
}

/**
 * Pick a light of dark color based on whichever one has better contrast
 */
export function bestContrast(c: string, light = COLOR_WHITE, dark = COLOR_BLACK): string {
  return contrastRatio(c, light) > contrastRatio(c, dark) ? light : dark;
}

/**
 * Pick a light of dark color based on whichever one has better contrast
 */
export function contrastBorder(
  c: string,
  threshold = 1.1,
  background = COLOR_WHITE,
  border = COLOR_BLACK
): string {
  const contrast = contrastRatio(c, background);
  if (contrast < threshold) {
    return mixColors(c, border, 0.1);
  }
  return c;
}

export function createColorRange(
  xFn: number | EasingFunction,
  yFn: number | EasingFunction,
  zFn: number | EasingFunction,
  createColor: (x: number, y: number, z: number) => string,
  range: number | number[] = 11
): string[] {
  const stops: number[] =
    typeof range === 'number' ? new Array(range).fill(0).map((_v, i) => i / (range - 1)) : range;
  return stops.map((t) => {
    const x = typeof xFn === 'function' ? xFn(t) : xFn;
    const y = typeof yFn === 'function' ? yFn(t) : yFn;
    const z = typeof zFn === 'function' ? zFn(t) : zFn;
    return createColor(x, y, z);
  });
}

export function createColorRangeFromStops(
  values: [string | Color, number][] | (string | Color)[],
  x: number | EasingFunction,
  y: number | EasingFunction,
  z: number | EasingFunction,
  format: 'lab' | 'rgb' = 'lab',
  range: number | number[] = 11
): string[] {
  const numValues = values.length;
  const lastValueIndex = numValues - 1;
  const colors = new Array(numValues);
  const stops = new Array(numValues);
  values.forEach((value: typeof values[0], i: number) => {
    const [color, stop] = Array.isArray(value) ? value : [value, i / lastValueIndex];
    colors[i] = color instanceof Color ? color : new Color(color);
    stops[i] = stop;
  });

  switch (format) {
    case 'rgb':
      return createColorRange(
        createStopEasingFn(x, (color) => color.getRed() / 255, colors, stops),
        createStopEasingFn(y, (color) => color.getGreen() / 255, colors, stops),
        createStopEasingFn(z, (color) => color.getBlue() / 255, colors, stops),
        (r, g, b) => Color.rgb(r, g, b, true).hex(),
        range
      );
    case 'lab':
    default: {
      return createColorRange(
        createStopEasingFn(x, (color) => color.getLabL() / 100, colors, stops),
        createStopEasingFn(y, (color) => (color.getLabA() + 128) / 255, colors, stops),
        createStopEasingFn(z, (color) => (color.getLabB() + 128) / 255, colors, stops),
        (l, a, b) => Color.lab(l, a, b, true).hex(),
        range
      );
    }
  }
}

export function createHsbColorRange(
  hue: number | EasingFunction,
  saturation: number | EasingFunction,
  brightness: number | EasingFunction,
  range: number | number[] = 11
): string[] {
  return createColorRange(
    hue,
    saturation,
    brightness,
    (h, s, b) => Color.hsb(h, s, b, true).hex(),
    range
  );
}

export function createHslColorRange(
  hue: number | EasingFunction,
  saturation: number | EasingFunction,
  lightness: number | EasingFunction,
  range: number | number[] = 11
): string[] {
  return createColorRange(
    hue,
    saturation,
    lightness,
    (h, s, l) => Color.hsl(h, s, l, true).hex(),
    range
  );
}

export function createHsluvColorRange(
  hue: number | EasingFunction,
  saturation: number | EasingFunction,
  lightness: number | EasingFunction,
  range: number | number[] = 11
): string[] {
  return createColorRange(
    hue,
    saturation,
    lightness,
    (h, s, l) => Color.hsluv(h, s, l, true).hex(),
    range
  );
}

export function createLabColorRange(
  lightness: number | EasingFunction,
  greenRed: number | EasingFunction,
  blueYellow: number | EasingFunction,
  range: number | number[] = 11
): string[] {
  return createColorRange(
    lightness,
    greenRed,
    blueYellow,
    (l, a, b) => Color.lab(l, a, b, true).hex(),
    range
  );
}
export function createRgbColorRange(
  red: number | EasingFunction,
  green: number | EasingFunction,
  blue: number | EasingFunction,
  range: number | number[] = 11
): string[] {
  return createColorRange(red, green, blue, (r, g, b) => Color.rgb(r, g, b, true).hex(), range);
}

export function formatLinearGradient(value: {
  angle: number;
  colors: string[];
  positions?: number[];
}) {
  const { angle = 0, colors: gradientColors, positions = [] } = value;

  // Represent a single color as a solid color rather than a gradient
  if (gradientColors.length === 1) {
    return gradientColors[0];
  }

  // We can omit the color positions in CSS if they are evenly-spaced
  const colors = hasLinearPositions(positions)
    ? gradientColors
    : gradientColors.map((color, i) =>
        positions[i] == null ? color : `${color} ${positions[i] * 100}%`
      );

  let direction = `${angle || 0}deg`;

  switch (angle) {
    case 45:
      direction = 'to top right';
      break;
    case 135:
      direction = 'to bottom right';
      break;
    case 225:
      direction = 'to bottom left';
      break;
    case 315:
      direction = 'to top left';
      break;
  }

  return `linear-gradient(${direction}, ${colors.join(', ')})`;
}

export function hasLinearPositions(positions: number[]) {
  const total = positions.length - 1;
  return positions.reduce(
    (acc, position, i) => acc && (position == null || position === i / total),
    true
  );
}
